import React, { useContext, useEffect, useState, useRef } from 'react'
import { FaPlus, FaSpinner } from 'react-icons/fa'
import { BASE_URL_API } from '../../References/URLs'
import AuthContext from '../../Store/AuthContext'
import styles from './UserAddress.module.css'
import { motion } from 'framer-motion'
import { MdDeleteForever } from 'react-icons/md'
import { toast } from 'react-toastify'
import { humanReadableTimePassed } from '../../Helper/calculations'

const UserAddress = (props) => {
    const authCtx = useContext(AuthContext)
    const isLoggedIn = authCtx.isLoggedIn
    const loginData = JSON.parse(authCtx.loginData)
    const [newAddressFormOpen, setNewAddressFormOpen] = useState(false)
    const [addresses, setAddresses] = useState([])
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        fullName: '',
        phone: '',
        address: '',
        pincode: '',
        addressNickname: '',
        addressType: 'Home', // Default value
    })
    const [formErrors, setFormErrors] = useState({
        fullName: '',
        phone: '',
        address: '',
        pincode: '',
        addressNickname: '',
    })

    // Refs for the form fields
    const fullNameRef = useRef(null)
    const phoneRef = useRef(null)
    const addressRef = useRef(null)
    const pincodeRef = useRef(null)
    const addressNicknameRef = useRef(null)

    const { selectedAddress, selectAddressHandler } = props

    // Fetch user addresses
    const getUserAddresses = async () => {
        setLoading(true)
        const response = await fetch(BASE_URL_API + "getUserAddresses", {
            method: "POST",
            body: JSON.stringify({
                token: authCtx.token,
                userIdentifier: loginData?.userIdentifier,
            }),
        });

        if (!response.ok) {
            console.log("Server Error");
        } else {
            const data = await response.json();
            if (data.status === "success") {
                setAddresses(data.response);
            } else {
                if (data.message === "token Expired, please login again!") {
                    authCtx.logout();
                } else {
                    setAddresses([]);
                }
            }
        }
        setLoading(false)
    };

    useEffect(() => {
        getUserAddresses()
    }, [])

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        })
    }

    const validateForm = () => {
        let isValid = true
        let errors = {
            fullName: '',
            phone: '',
            address: '',
            pincode: '',
            addressNickname: '',
        }

        if (!formData.fullName) {
            errors.fullName = 'Receiver name is required'
            isValid = false
        }
        if (!formData.phone) {
            errors.phone = 'Receiver phone is required'
            isValid = false
        }
        if (!formData.address) {
            errors.address = 'Address is required'
            isValid = false
        }
        if (!formData.pincode) {
            errors.pincode = 'Pincode is required'
            isValid = false
        }
        if (!formData.addressNickname) {
            errors.addressNickname = 'Address nickname is required'
            isValid = false
        }

        setFormErrors(errors)

        // Focus on the first input field with error
        if (!formData.fullName && fullNameRef.current) {
            fullNameRef.current.focus()
        } else if (!formData.phone && phoneRef.current) {
            phoneRef.current.focus()
        } else if (!formData.address && addressRef.current) {
            addressRef.current.focus()
        } else if (!formData.pincode && pincodeRef.current) {
            pincodeRef.current.focus()
        } else if (!formData.addressNickname && addressNicknameRef.current) {
            addressNicknameRef.current.focus()
        }

        return isValid
    }

    const handleSubmitNewAddress = async () => {
        if (validateForm()) {
            console.log(formData)
            // Submit the new address
            const response = await fetch(BASE_URL_API + "addUserAddress", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    userIdentifier: loginData?.userIdentifier,
                    ...formData,
                }),
            });
            const data = await response.json();
            if (data.status === "success") {
                setNewAddressFormOpen(false);
                setFormData({
                    address: '',
                    pincode: '',
                    phone: '',
                    fullName: '',
                    addressNickname: '',
                    addressType: 'Home',
                })
                getUserAddresses();
                // console.log(data.message)
            } else {
                console.log('Error adding address:', data.message);
            }
        }
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmitNewAddress();
        }
    }

    const deleteUserAddress = async (k) => {
        if (window.confirm("Do you really want to delete this address?")) {
            const response = await fetch(BASE_URL_API + "deleteUserAddress",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        addressIdentifier: k?.addressIdentifier
                    })
                });

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
            } else {
                const respo = await response.json()
                if (respo.status === "success") {
                    toast(`User address deleted successfully!`)
                    getUserAddresses()
                } else {
                    if (respo?.message === "token Expired, please login again!") {
                        authCtx.logout()
                    }
                }
                console.log(respo)
            }

        }
    }

    return (
        <div>
            <div className={styles.addressSection}>
                <div className={styles.sectionTitle}>
                    <div>
                        <div>
                            Select Delivery Address
                        </div>
                        <div className={styles.addressCount}>{addresses?.length} addresses available</div>
                    </div>
                    <button className={styles.addMoreAddress} onClick={() => setNewAddressFormOpen(true)}>
                        <FaPlus /> Add New
                    </button>
                </div>
                {newAddressFormOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={styles.newAddressForm}
                    >
                        <div className={styles.aaTwoItems}>
                            <div className={styles.eachOfTwo}>
                                <input
                                    name="fullName"
                                    placeholder="Receiver Name"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyPress}
                                    ref={fullNameRef}
                                    autoFocus
                                />
                                {formErrors.fullName && <div className={styles.error}>{formErrors.fullName}</div>}
                            </div>
                            <div className={styles.eachOfTwo}>
                                <input
                                    name="phone"
                                    placeholder="Receiver Phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyPress}
                                    ref={phoneRef}
                                />
                                {formErrors.phone && <div className={styles.error}>{formErrors.phone}</div>}
                            </div>
                        </div>
                        <div>
                            <textarea
                                name="address"
                                placeholder="Address Details"
                                value={formData.address?.trim()}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyPress}
                                ref={addressRef}
                            />
                            {formErrors.address && <div className={styles.error}>{formErrors.address}</div>}
                        </div>
                        <div>
                            <input
                                name="pincode"
                                placeholder="Enter Pincode"
                                value={formData.pincode}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyPress}
                                ref={pincodeRef}
                            />
                            {formErrors.pincode && <div className={styles.error}>{formErrors.pincode}</div>}
                        </div>
                        <div className={styles.aaTwoItems}>
                            <div>
                                <input
                                    name="addressNickname"
                                    placeholder="Address Nickname"
                                    value={formData.addressNickname}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyPress}
                                    ref={addressNicknameRef}
                                />
                                {formErrors.addressNickname && <div className={styles.error}>{formErrors.addressNickname}</div>}
                            </div>
                            <div>
                                <select
                                    name="addressType"
                                    value={formData.addressType}
                                    onChange={handleInputChange}
                                >
                                    <option value="Home">Home</option>
                                    <option value="Office">Office</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>
                        <div className={styles.aaAction}>
                            <button onClick={handleSubmitNewAddress}>Submit Address</button>
                            <button className={styles.cancelBtn} onClick={() => setNewAddressFormOpen(false)}>Cancel</button>
                        </div>
                    </motion.div>
                )}

                {loading ? (
                    <div className={styles.loaderContainer}>
                        <FaSpinner className={styles.spinner} />
                    </div>
                ) : (
                    <div className={styles.addressList}>
                        {addresses.map((address) => (
                            <motion.div
                                key={address.id}
                                className={`${styles.addressCard} ${selectedAddress?.addressIdentifier === address.addressIdentifier ? styles.selected : ''}`}
                                onClick={() => selectAddressHandler(address)}
                                whileHover={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 200 }}
                            >
                                <div>
                                    <div className={styles.fullName}>
                                        {address.fullName}, {address?.phoneNumber}
                                    </div>
                                    <div className={styles.fullAddress}>{address.address}</div>
                                    <div className={styles.addedOn}>
                                        Added on {humanReadableTimePassed(address?.addedOn)}
                                    </div>
                                </div>
                                <div className={styles.deleteWrapper}>
                                    <button onClick={() => deleteUserAddress(address)} >
                                        <MdDeleteForever size={20} color="#818181" />
                                    </button>
                                </div>

                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default UserAddress
